import React, { useState, useEffect, useMemo } from 'react';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Upload,
  Search,
  Filter,
  MoreHorizontal,
  FileText,
  Eye,
  Edit,
  Trash,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  FileUp,
  Hash,
  FileCode,
  BookOpen,
  Shield,
  DollarSign,
  GraduationCap,
  Loader2,
  Building,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { uploadDocument, getDocuments, deleteDocument, updateDocument } from '@/services/aiDocumentService';
import type { AIDocument, DocumentType, ComplianceTag } from '@/types/ai-admin';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('AIDocumentManager', 'ai-documents');

// Document type icons
const documentTypeIcons: Record<DocumentType, React.ReactNode> = {
  api_doc: <FileCode className="h-4 w-4" />,
  compliance: <Shield className="h-4 w-4" />,
  guideline: <BookOpen className="h-4 w-4" />,
  product: <DollarSign className="h-4 w-4" />,
  training: <GraduationCap className="h-4 w-4" />
};

// Document type colors
const documentTypeColors: Record<DocumentType, string> = {
  api_doc: 'bg-blue-100 text-blue-800',
  compliance: 'bg-red-100 text-red-800',
  guideline: 'bg-green-100 text-green-800',
  product: 'bg-purple-100 text-purple-800',
  training: 'bg-yellow-100 text-yellow-800'
};

export default function AIDocumentManager() {
  const [documents, setDocuments] = useState<AIDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<DocumentType | 'all'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'citations'>('date');
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [groupByInvestor, setGroupByInvestor] = useState(true);
  const [expandedInvestors, setExpandedInvestors] = useState<Set<string>>(new Set());

  // Upload state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadType, setUploadType] = useState<DocumentType>('training');
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadKeywords, setUploadKeywords] = useState('');
  const [uploadComplianceTags, setUploadComplianceTags] = useState<ComplianceTag[]>([]);
  const [uploadExpiresAt, setUploadExpiresAt] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Preview/Edit state
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<AIDocument | null>(null);
  const [editingDocument, setEditingDocument] = useState<AIDocument | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Load documents on mount
  useEffect(() => {
    loadDocuments();
  }, []);

  async function loadDocuments() {
    try {
      setIsLoading(true);
      const docs = await getDocuments();
      setDocuments(docs);
      log.info('Documents loaded', { count: docs.length });
    } catch (error) {
      log.error('Failed to load documents', {}, error as Error);
      toast.error('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  }

  // Filter and sort documents
  const filteredDocuments = useMemo(() => {
    let filtered = [...documents]; // Spread to avoid mutating state

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        doc =>
          doc.title.toLowerCase().includes(query) ||
          (doc.keywords || []).some(k => k.toLowerCase().includes(query)) ||
          doc.category.toLowerCase().includes(query)
      );
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(doc => doc.documentType === filterType);
    }

    // Sort
    switch (sortBy) {
      case 'name':
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'citations':
        filtered.sort((a, b) => b.citationCount - a.citationCount);
        break;
      case 'date':
      default:
        filtered.sort((a, b) =>
          new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
        );
    }

    return filtered;
  }, [documents, searchQuery, filterType, sortBy]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalChunks = documents.reduce((sum, doc) => sum + (doc.chunks || []).length, 0);
    const totalSize = documents.reduce((sum, doc) => sum + doc.fileSize, 0);
    const expiredCount = documents.filter(doc =>
      doc.expiresAt && new Date(doc.expiresAt) < new Date()
    ).length;

    return {
      totalDocuments: documents.length,
      totalChunks,
      totalSizeMB: (totalSize / 1024 / 1024).toFixed(1),
      expiredCount
    };
  }, [documents]);

  // Group documents by investor
  const documentsByInvestor = useMemo(() => {
    const groups: Record<string, AIDocument[]> = {};

    filteredDocuments.forEach(doc => {
      const key = doc.investorName || 'Other Documents';
      if (!groups[key]) {
groups[key] = [];
}
      groups[key].push(doc);
    });

    // Sort investor groups: alphabetically, with "Other Documents" at the end
    return Object.entries(groups).sort(([a], [b]) =>
      a === 'Other Documents' ? 1 : b === 'Other Documents' ? -1 : a.localeCompare(b)
    );
  }, [filteredDocuments]);

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      // Auto-populate title from filename
      if (!uploadTitle) {
        setUploadTitle(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  // Handle document upload
  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    if (!uploadTitle.trim()) {
      toast.error('Please enter a document title');
      return;
    }

    if (!uploadCategory.trim()) {
      toast.error('Please enter a category');
      return;
    }

    const keywords = uploadKeywords.split(',').map(k => k.trim()).filter(k => k.length > 0);
    if (keywords.length === 0) {
      toast.error('Please enter at least one keyword');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const doc = await uploadDocument({
        file: uploadFile,
        title: uploadTitle.trim(),
        documentType: uploadType,
        category: uploadCategory.trim(),
        keywords,
        complianceTags: uploadComplianceTags.length > 0 ? uploadComplianceTags : undefined,
        expiresAt: uploadExpiresAt || undefined,
        onProgress: setUploadProgress
      });

      setDocuments(prev => [doc, ...prev]);
      toast.success(`Document "${doc.title}" uploaded successfully`);

      // Reset form
      setUploadFile(null);
      setUploadTitle('');
      setUploadCategory('');
      setUploadKeywords('');
      setUploadComplianceTags([]);
      setUploadExpiresAt('');
      setUploadProgress(0);
      setShowUploadDialog(false);

      log.info('Document uploaded', { documentId: doc.id, title: doc.title });
    } catch (error) {
      log.error('Upload failed', { fileName: uploadFile.name }, error as Error);
      toast.error((error as Error).message || 'Failed to upload document');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted successfully');
    } catch (error) {
      log.error('Delete failed', { documentId: docId }, error as Error);
      toast.error('Failed to delete document');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocuments.length === 0) {
return;
}

    // eslint-disable-next-line no-alert
    if (!window.confirm(`Are you sure you want to delete ${selectedDocuments.length} document(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      await Promise.all(selectedDocuments.map(id => deleteDocument(id)));
      setDocuments(prev => prev.filter(d => !selectedDocuments.includes(d.id)));
      setSelectedDocuments([]);
      toast.success(`${selectedDocuments.length} document(s) deleted successfully`);
    } catch (error) {
      log.error('Bulk delete failed', {}, error as Error);
      toast.error('Failed to delete some documents');
    }
  };

  const handleEditSave = async () => {
    if (!editingDocument) {
return;
}

    try {
      await updateDocument(editingDocument.id, {
        title: editingDocument.title,
        category: editingDocument.category,
        keywords: editingDocument.keywords,
        complianceTags: editingDocument.complianceTags,
        expiresAt: editingDocument.expiresAt
      });

      setDocuments(prev =>
        prev.map(d => (d.id === editingDocument.id ? editingDocument : d))
      );

      toast.success('Document updated successfully');
      setShowEditDialog(false);
      setEditingDocument(null);
    } catch (error) {
      log.error('Update failed', { documentId: editingDocument.id }, error as Error);
      toast.error('Failed to update document');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) {
return `${bytes  } B`;
}
    if (bytes < 1024 * 1024) {
return `${(bytes / 1024).toFixed(1)  } KB`;
}
    return `${(bytes / 1024 / 1024).toFixed(1)  } MB`;
  };

  const formatTokenCount = (tokens?: number): string => {
    // Handle undefined, null, or invalid values
    if (tokens === undefined || tokens === null || isNaN(tokens)) {
      return '0';
    }

    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    } else if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toString();
  };

  const formatDate = (date: string): string => {
    const d = new Date(date);
    // Show full date and time
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 text-muted-foreground mx-auto animate-spin" />
          <p className="text-muted-foreground">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Document Library</h2>
          <p className="text-muted-foreground">
            Manage knowledge documents for the Encompass Helper AI
          </p>
        </div>
        <Button onClick={() => setShowUploadDialog(true)} size="lg">
          <Upload className="mr-2 h-4 w-4" />
          Upload Documents
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Documents</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalDocuments}</div>
            <p className="text-xs text-muted-foreground">
              {stats.totalChunks} chunks indexed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Storage Used</CardTitle>
            <Hash className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalSizeMB} MB</div>
            <p className="text-xs text-muted-foreground">
              Across all documents
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Documents</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalDocuments - stats.expiredCount}
            </div>
            <p className="text-xs text-muted-foreground">
              Ready for AI queries
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expired</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.expiredCount}</div>
            <p className="text-xs text-muted-foreground">
              Need review/update
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-2 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 w-[300px]"
                />
              </div>

              <Select value={filterType} onValueChange={(value: string) => setFilterType(value)}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="api_doc">API Documentation</SelectItem>
                  <SelectItem value="compliance">Compliance</SelectItem>
                  <SelectItem value="guideline">Guidelines</SelectItem>
                  <SelectItem value="product">Product Info</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={sortBy}
                onValueChange={(value) => {
                  if (value === 'date' || value === 'name' || value === 'citations') {
                    setSortBy(value);
                  }
                }}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Upload Date</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="citations">Citations</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2 px-2 border-l">
                <Switch
                  id="group-by-investor"
                  checked={groupByInvestor}
                  onCheckedChange={setGroupByInvestor}
                />
                <Label htmlFor="group-by-investor" className="text-sm cursor-pointer whitespace-nowrap">
                  Group by Investor
                </Label>
              </div>

              <Button variant="ghost" size="icon" onClick={loadDocuments}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>

            {selectedDocuments.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">
                  {selectedDocuments.length} selected
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                >
                  Delete Selected
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {documents.length === 0
                  ? 'No documents uploaded yet. Upload your first document to get started.'
                  : 'No documents match your search criteria.'}
              </p>
            </div>
          ) : groupByInvestor ? (
            /* Grouped by Investor View */
            <div className="space-y-3">
              {documentsByInvestor.map(([investorName, docs]) => {
                const isExpanded = expandedInvestors.has(investorName);
                const allSelected = docs.every(d => selectedDocuments.includes(d.id));
                const someSelected = docs.some(d => selectedDocuments.includes(d.id));

                return (
                  <Collapsible
                    key={investorName}
                    open={isExpanded}
                    onOpenChange={(open) => {
                      const next = new Set(expandedInvestors);
                      if (open) {
                        next.add(investorName);
                      } else {
                        next.delete(investorName);
                      }
                      setExpandedInvestors(next);
                    }}
                  >
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) {
el.indeterminate = someSelected && !allSelected;
}
                        }}
                        onChange={(e) => {
                          const docIds = docs.map(d => d.id);
                          if (e.target.checked) {
                            setSelectedDocuments([...new Set([...selectedDocuments, ...docIds])]);
                          } else {
                            setSelectedDocuments(selectedDocuments.filter(id => !docIds.includes(id)));
                          }
                        }}
                        className="h-4 w-4"
                      />
                      <CollapsibleTrigger className="flex-1 flex items-center gap-2 text-left">
                        {investorName === 'Other Documents' ? (
                          <FileText className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Building className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="font-medium">{investorName}</span>
                        <Badge variant="secondary">{docs.length} docs</Badge>
                        <ChevronDown className={`h-4 w-4 ml-auto transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent className="mt-1">
                      <div className="rounded-md border ml-6">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[50px]"></TableHead>
                              <TableHead>Document</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Chunks</TableHead>
                              <TableHead>Citations</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {docs.map((doc) => (
                              <TableRow key={doc.id}>
                                <TableCell>
                                  <input
                                    type="checkbox"
                                    checked={selectedDocuments.includes(doc.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedDocuments([...selectedDocuments, doc.id]);
                                      } else {
                                        setSelectedDocuments(selectedDocuments.filter(id => id !== doc.id));
                                      }
                                    }}
                                    className="h-4 w-4"
                                  />
                                </TableCell>
                                <TableCell>
                                  <div>
                                    <div className="font-medium text-sm">{doc.title}</div>
                                    <div className="text-xs text-muted-foreground">
                                      {(doc.keywords || []).slice(0, 3).join(', ')}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className={`text-xs ${documentTypeColors[doc.documentType]}`}>
                                    {doc.documentType.replace('_', ' ')}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-sm">{(doc.chunks || []).length}</TableCell>
                                <TableCell className="text-sm">{doc.citationCount}</TableCell>
                                <TableCell>
                                  {['ready', 'completed'].includes(doc.status) ? (
                                    <Badge variant="outline" className="bg-green-100 text-green-800 text-xs">
                                      {doc.status === 'ready' ? 'Ready' : 'Completed'}
                                    </Badge>
                                  ) : ['processing', 'extracted', 'classified', 'queued'].includes(doc.status) ? (
                                    <Badge variant="outline" className="bg-yellow-100 text-yellow-800 text-xs">
                                      {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="bg-red-100 text-red-800 text-xs">Error</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" className="h-8 w-8 p-0">
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => {
 setPreviewDocument(doc); setShowPreviewDialog(true); 
}}>
                                        <Eye className="mr-2 h-4 w-4" />Preview
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => {
 setEditingDocument(doc); setShowEditDialog(true); 
}}>
                                        <Edit className="mr-2 h-4 w-4" />Edit
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => handleDeleteDocument(doc.id)} className="text-red-600">
                                        <Trash className="mr-2 h-4 w-4" />Delete
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          ) : (
            /* Flat Table View */
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <input
                        type="checkbox"
                        checked={selectedDocuments.length === filteredDocuments.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDocuments(filteredDocuments.map(d => d.id));
                          } else {
                            setSelectedDocuments([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Document</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Chunks</TableHead>
                    <TableHead>Tokens</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Citations</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocuments.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedDocuments.includes(doc.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDocuments([...selectedDocuments, doc.id]);
                            } else {
                              setSelectedDocuments(selectedDocuments.filter(id => id !== doc.id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{doc.title}</div>
                          <div className="text-sm text-muted-foreground">
                            {(doc.keywords || []).slice(0, 3).join(', ')}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={documentTypeColors[doc.documentType]}
                        >
                          {documentTypeIcons[doc.documentType]}
                          <span className="ml-1 capitalize">
                            {doc.documentType.replace('_', ' ')}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell>{(doc.chunks || []).length}</TableCell>
                      <TableCell>{formatTokenCount(doc.totalTokens)}</TableCell>
                      <TableCell>{formatFileSize(doc.fileSize)}</TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <span>{doc.citationCount}</span>
                          {doc.relevanceScore && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              ({(doc.relevanceScore * 100).toFixed(0)}%)
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {['ready', 'completed'].includes(doc.status) ? (
                          <Badge variant="outline" className="bg-green-100 text-green-800">
                            <CheckCircle className="mr-1 h-3 w-3" />
                            {doc.status === 'ready' ? 'Ready' : 'Completed'}
                          </Badge>
                        ) : ['processing', 'extracted', 'classified', 'queued'].includes(doc.status) ? (
                          <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                            <Clock className="mr-1 h-3 w-3" />
                            {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-100 text-red-800">
                            <AlertCircle className="mr-1 h-3 w-3" />
                            Error
                          </Badge>
                        )}
                        {doc.expiresAt && new Date(doc.expiresAt) < new Date() && (
                          <Badge variant="destructive" className="ml-2">
                            Expired
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(doc.uploadedAt)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() => {
                                setPreviewDocument(doc);
                                setShowPreviewDialog(true);
                              }}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              Preview
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setEditingDocument(doc);
                                setShowEditDialog(true);
                              }}
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              Edit Metadata
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDeleteDocument(doc.id)}
                              className="text-red-600"
                            >
                              <Trash className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Documents</DialogTitle>
            <DialogDescription>
              Add new documents to the AI knowledge base
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <FileUp className="mx-auto h-12 w-12 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                {uploadFile ? uploadFile.name : 'Select a file to upload'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Supports PDF, TXT, MD files up to 50MB
              </p>
              <Input
                type="file"
                accept=".pdf,.txt,.md"
                onChange={handleFileSelect}
                className="mt-4"
              />
            </div>

            {isUploading && (
              <div className="space-y-2">
                <Label>Upload Progress</Label>
                <Progress value={uploadProgress} />
                <p className="text-xs text-muted-foreground">{uploadProgress}% complete</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Document Title *</Label>
              <Input
                placeholder="e.g., Encompass API Authentication Guide"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                disabled={isUploading}
              />
            </div>

            <div className="space-y-2">
              <Label>Document Type *</Label>
              <Select value={uploadType} onValueChange={(value: string) => setUploadType(value)} disabled={isUploading}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="api_doc">API Documentation</SelectItem>
                  <SelectItem value="compliance">Compliance</SelectItem>
                  <SelectItem value="guideline">Guidelines</SelectItem>
                  <SelectItem value="product">Product Info</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Category *</Label>
              <Input
                placeholder="e.g., Encompass API, RESPA, FHA"
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                disabled={isUploading}
              />
            </div>

            <div className="space-y-2">
              <Label>Keywords (comma-separated) *</Label>
              <Input
                placeholder="e.g., loan, API, authentication"
                value={uploadKeywords}
                onChange={(e) => setUploadKeywords(e.target.value)}
                disabled={isUploading}
              />
            </div>

            <div className="space-y-2">
              <Label>Compliance Tags (optional)</Label>
              <div className="flex flex-wrap gap-2">
                {(['RESPA', 'TILA', 'HMDA', 'FCRA', 'ECOA', 'FHA', 'VA'] as ComplianceTag[]).map((tag) => (
                  <label key={tag} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={uploadComplianceTags.includes(tag)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setUploadComplianceTags([...uploadComplianceTags, tag]);
                        } else {
                          setUploadComplianceTags(uploadComplianceTags.filter(t => t !== tag));
                        }
                      }}
                      disabled={isUploading}
                    />
                    <span className="text-sm">{tag}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Expiration Date (optional)</Label>
              <Input
                type="date"
                value={uploadExpiresAt}
                onChange={(e) => setUploadExpiresAt(e.target.value)}
                disabled={isUploading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)} disabled={isUploading}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={isUploading || !uploadFile}>
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{previewDocument?.title}</DialogTitle>
            <DialogDescription>
              Document preview and chunk information
            </DialogDescription>
          </DialogHeader>
          {previewDocument && (
            <Tabs defaultValue="content" className="mt-4">
              <TabsList>
                <TabsTrigger value="content">Content</TabsTrigger>
                <TabsTrigger value="chunks">Chunks ({previewDocument.chunks.length})</TabsTrigger>
                <TabsTrigger value="metadata">Metadata</TabsTrigger>
              </TabsList>
              <TabsContent value="content">
                <ScrollArea className="h-[400px] w-full rounded-md border p-4">
                  <pre className="whitespace-pre-wrap font-mono text-sm">
                    {previewDocument.content}
                  </pre>
                </ScrollArea>
              </TabsContent>
              <TabsContent value="chunks">
                <ScrollArea className="h-[400px] w-full">
                  <div className="space-y-2 p-4">
                    {previewDocument.chunks.map((chunk, index) => (
                      <Card key={index}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm">
                              Chunk {index + 1}
                            </CardTitle>
                            <Badge variant="outline">
                              {chunk.tokenCount} tokens
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground line-clamp-3">
                            {chunk.content}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
              <TabsContent value="metadata">
                <div className="space-y-4 p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Document ID</Label>
                      <p className="text-sm text-muted-foreground">{previewDocument.id}</p>
                    </div>
                    <div>
                      <Label>Type</Label>
                      <p className="text-sm text-muted-foreground capitalize">
                        {previewDocument.documentType.replace('_', ' ')}
                      </p>
                    </div>
                    <div>
                      <Label>Category</Label>
                      <p className="text-sm text-muted-foreground">{previewDocument.category}</p>
                    </div>
                    <div>
                      <Label>File Size</Label>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(previewDocument.fileSize)}
                      </p>
                    </div>
                    <div>
                      <Label>Citation Count</Label>
                      <p className="text-sm text-muted-foreground">
                        {previewDocument.citationCount} times
                      </p>
                    </div>
                    <div>
                      <Label>Relevance Score</Label>
                      <p className="text-sm text-muted-foreground">
                        {previewDocument.relevanceScore
                          ? `${(previewDocument.relevanceScore * 100).toFixed(0)}%`
                          : 'N/A'}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <Label>Keywords</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {previewDocument.keywords.map((keyword, index) => (
                          <Badge key={index} variant="secondary">
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Document Metadata</DialogTitle>
            <DialogDescription>
              Update document information and settings
            </DialogDescription>
          </DialogHeader>
          {editingDocument && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editingDocument.title}
                  onChange={(e) =>
                    setEditingDocument({ ...editingDocument, title: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <Input
                  value={editingDocument.category}
                  onChange={(e) =>
                    setEditingDocument({ ...editingDocument, category: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Keywords</Label>
                <Textarea
                  value={editingDocument.keywords.join(', ')}
                  onChange={(e) =>
                    setEditingDocument({
                      ...editingDocument,
                      keywords: e.target.value.split(',').map(k => k.trim())
                    })
                  }
                  placeholder="Comma-separated keywords"
                />
              </div>

              <div className="space-y-2">
                <Label>Expiration Date</Label>
                <Input
                  type="date"
                  value={
                    editingDocument.expiresAt
                      ? new Date(editingDocument.expiresAt).toISOString().split('T')[0]
                      : ''
                  }
                  onChange={(e) =>
                    setEditingDocument({
                      ...editingDocument,
                      expiresAt: e.target.value ? parseAsLocalDate(e.target.value).toISOString() : undefined
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Compliance Tags</Label>
                <div className="flex flex-wrap gap-2">
                  {(['RESPA', 'TILA', 'HMDA', 'FCRA', 'ECOA', 'FHA', 'VA'] as ComplianceTag[]).map((tag) => (
                    <label key={tag} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={editingDocument.complianceTags?.includes(tag)}
                        onChange={(e) => {
                          const tags = editingDocument.complianceTags || [];
                          if (e.target.checked) {
                            setEditingDocument({
                              ...editingDocument,
                              complianceTags: [...tags, tag]
                            });
                          } else {
                            setEditingDocument({
                              ...editingDocument,
                              complianceTags: tags.filter(t => t !== tag)
                            });
                          }
                        }}
                      />
                      <span className="text-sm">{tag}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSave}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
